const LABEL = /macro|hamming|signature|recognition|screenshot/i;
const IDS = new Set(['requestId', 'runId', 'campaignId', 'jobId', 'nodeId', 'candidateId', 'calibrationId',
  'launchInstanceId', 'currentNodeId', 'rootNodeId', 'takeId', 'screenStateId']);
const NUMBERS = new Set(['score', 'distance', 'runnerUpDistance', 'luminanceDifference', 'fusedDistance',
  'quality', 'captureToDecisionMs', 'frameSequence', 'schemaVersion', 'durationMs', 'elapsedMs', 'count']);
const CODES = new Set(['component', 'operation', 'op', 'outcome', 'errorCode', 'reason', 'decision', 'mode', 'profile',
  'preflightStage']);

/** Bounded recognition covers console arguments and serialized IPC. */
export class MacroTelemetrySanitizer {
  public static recognizes(value: unknown, depth = 0, seen = new Set<object>()): boolean {
    if (typeof value === 'string') return value.length > 16384 || LABEL.test(value);
    if (!value || typeof value !== 'object') return false;
    if (depth > 4 || seen.has(value)) return true;
    seen.add(value);
    let count = 0;
    for (const key in value) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      if (++count > 64 || LABEL.test(key)) return true;
      if (this.recognizes((value as Record<string, unknown>)[key], depth + 1, seen)) return true;
    }
    return false;
  }

  public static sanitize(value: unknown, depth = 0, seen = new Set<object>()): Record<string, unknown> {
    if (!value || typeof value !== 'object' || depth > 4 || seen.has(value)) return {};
    seen.add(value);
    const result: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value).slice(0, 128)) {
      if (IDS.has(key) && typeof child === 'string') result[key] = child.slice(0, 128);
      else if (NUMBERS.has(key) && typeof child === 'number' && Number.isFinite(child)) result[key] = child;
      else if (CODES.has(key) && typeof child === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(child)) result[key] = child;
      else if (key === 'revisionPrefix' && typeof child === 'string' && /^[a-f0-9]{1,12}$/.test(child)) result[key] = child;
      else if (['inputsSent', 'measurementsTruncated', 'confirmed'].includes(key) && typeof child === 'boolean') result[key] = child;
      else if (key === 'measurements' && Array.isArray(child) && depth < 4) {
        result[key] = child.slice(0, 64).map(item => this.sanitize(item, depth + 1, seen));
      }
    }
    return result;
  }
}
